import { Button, Checkbox, Input, Popup, TextArea } from '@sam/components';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Car, MonthlyReceipt, SchambeckUser, useData } from 'shared';
import { generateEmptyBonusPayout } from '../../utils/monthlyReceipt/MonthlyReceipt.utils';
import { generateEmptyAzkReduction } from '../../utils/user/User.utils';
import { useUser } from '../UserContext';
import { IconComponent, IconType } from '../icons/IconComponent';

/**
 * Component to display and edit data of a monthlyReceipt
 * @param monthlyReceipt to display and edit
 * @param setMonthlyOverviews setter for monthly overviews that include the monthlyReceipt
 * @param monthlyReceipts list of all monthlyReceipts
 * @returns  JSX with the detailBox
 */
const ReceiptDetailBox: React.FC<{
  monthlyReceipt: MonthlyReceipt;
  receiptUser: SchambeckUser | undefined;
  setMonthlyOverviews: Dispatch<SetStateAction<MonthlyReceipt[]>>;
  monthlyReceipts: MonthlyReceipt[];
}> = ({ receiptUser, monthlyReceipt, setMonthlyOverviews, monthlyReceipts }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [car, setCar] = useState<Car>();
  const [showAzkReductionPopup, toggleAzkReductionPopup] = useState<boolean>(false);
  const { data: cars } = useData('CARS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: currencies } = useData('CURRENCY_ALL_ACTIVE', { config: { fallbackData: [] } });
  /**
   * useEffect hook to set a bike or a car of a receipt, if the receipt contains the id
   */
  useEffect(() => {
    if (monthlyReceipt.car) setCar(cars.find((car) => car.id === monthlyReceipt.car));
  }, [cars, monthlyReceipt.car]);

  const currencySymbol: string = useMemo(
    (): string =>
      currencies.find((curr) => curr.id === receiptUser?.contractInformation.salaryCurrency)
        ?.symbol || '?',
    [receiptUser, currencies],
  );
  /**
   * Helper method to update the correct receipt on update
   * @param updatedReceipt the updated receipt entry
   */
  const updateReceipt = (updatedReceipt: MonthlyReceipt): void => {
    setMonthlyOverviews(
      monthlyReceipts.map((receipt) =>
        receipt.id === updatedReceipt.id ? updatedReceipt : receipt,
      ),
    );
  };

  /**
   * Decides if a receipt is locked and prevented from changes
   */
  const isLocked: boolean = useMemo(
    () => monthlyReceipt.softLocked || monthlyReceipt.completed,
    [monthlyReceipt.completed, monthlyReceipt.softLocked],
  );

  return (
    <div id="receipt-edit-form" className="receipt-detail-box">
      <TextArea
        label={t('pages.monthlySummary.children.additionalBenefits')}
        onChange={() => {}}
        resizable
        value={receiptUser?.contractInformation.additionalBenefits}
      />
      <div id="receipt-edit-form" className="receipt-detail-box__wrapper">
        <Popup
          onClose={() => toggleAzkReductionPopup(false)}
          title={t('pages.monthlySummary.children.popupTitle')}
          isOpen={showAzkReductionPopup}
          buttons={[
            <Button
              value={t('general.buttons.cancel')}
              onClick={() => toggleAzkReductionPopup(false)}
            />,
            <Button
              value={t('general.buttons.save')}
              onClick={() => {
                updateReceipt({
                  ...monthlyReceipt,
                  payedOutSaldo: monthlyReceipt.azkReductions.reduce(
                    (prev, curr) => prev + curr.amount,
                    0,
                  ),
                });
                toggleAzkReductionPopup(false);
              }}
            />,
          ]}
        >
          <IconComponent
            type={IconType.ADD}
            className={isLocked ? 'disabled' : 'clickable'}
            width={30}
            onClick={() => {
              if (isLocked) return;
              updateReceipt({
                ...monthlyReceipt,
                azkReductions: [
                  ...monthlyReceipt.azkReductions,
                  generateEmptyAzkReduction(user.id),
                ],
              });
            }}
          />

          {monthlyReceipt.azkReductions.map((reduction) => {
            return (
              <div className="receipt-detail-box__azk-entry">
                <IconComponent
                  type={IconType.DELETE}
                  className="receipt-detail-box__azk-entry__delete"
                  onClick={() =>
                    updateReceipt({
                      ...monthlyReceipt,
                      azkReductions: monthlyReceipt.azkReductions.filter(
                        (reductionEntry) => reductionEntry.id !== reduction.id,
                      ),
                    })
                  }
                />
                <Input
                  label={t('pages.monthlySummary.children.amount')}
                  value={reduction?.amount / 60}
                  type="number"
                  onChangeNumber={(amount) =>
                    updateReceipt({
                      ...monthlyReceipt,
                      azkReductions: monthlyReceipt.azkReductions.map((reductionEntry) =>
                        reductionEntry.id === reduction.id
                          ? { ...reduction, amount: amount * 60 }
                          : reductionEntry,
                      ),
                    })
                  }
                />
                <TextArea
                  resizable
                  value={reduction.comment}
                  label={t('pages.monthlySummary.children.reason')}
                  onChange={(comment) =>
                    updateReceipt({
                      ...monthlyReceipt,
                      azkReductions: monthlyReceipt.azkReductions.map((reductionEntry) =>
                        reductionEntry.id === reduction.id
                          ? { ...reduction, comment }
                          : reductionEntry,
                      ),
                    })
                  }
                />
              </div>
            );
          })}
        </Popup>
        <div className="receipt-detail-box__left-section">
          <div className="receipt-detail-box__box">
            <Checkbox
              onCheck={() => {}}
              isChecked={!!monthlyReceipt.car}
              label={t('pages.monthlySummary.children.car')}
              disabled
            />
            <div className="receipt-detail-box__box__double-input">
              <Input
                onChange={() => {}}
                type="text"
                value={car?.grossListPrice || 0}
                label={t('pages.monthlySummary.children.grossListPrice')}
                labelBold={false}
                disabled
              />
              <Input
                onChange={() => {}}
                labelBold={false}
                type="text"
                value={car?.registrationPlate || ''}
                label={t('pages.monthlySummary.children.licensePlate')}
                disabled
              />
            </div>
          </div>
          <div className="receipt-detail-box__box">
            <p>{t('pages.monthlySummary.children.travelCost')}</p>
            <div className="receipt-detail-box__box__double-input">
              <Input
                disabled={isLocked}
                labelBold={false}
                type="number"
                value={monthlyReceipt.travelCost?.km || 0}
                label={t('pages.monthlySummary.children.km', {
                  replace: { CURRENCY: currencySymbol },
                })}
                onChangeNumber={(km) =>
                  setMonthlyOverviews(
                    monthlyReceipts.map((receipt) =>
                      receipt.id === monthlyReceipt.id
                        ? {
                            ...receipt,
                            travelCost: { ...receipt.travelCost, km },
                          }
                        : receipt,
                    ),
                  )
                }
              />
              <Input
                disabled={isLocked}
                labelBold={false}
                type="number"
                value={monthlyReceipt.travelCost?.vma || 0}
                label={t('pages.monthlySummary.children.vma', {
                  replace: { CURRENCY: currencySymbol },
                })}
                onChangeNumber={(vma) =>
                  setMonthlyOverviews(
                    monthlyReceipts.map((receipt) =>
                      receipt.id === monthlyReceipt.id
                        ? {
                            ...receipt,
                            travelCost: { ...receipt.travelCost, vma },
                          }
                        : receipt,
                    ),
                  )
                }
              />
            </div>
          </div>
          <div className="receipt-detail-box__box">
            <p>{t('pages.monthlySummary.children.travelCostTaxFree')}</p>
            <div className="receipt-detail-box__box__double-input">
              <Input
                disabled={isLocked}
                labelBold={false}
                type="number"
                value={monthlyReceipt.travelCostTaxFree?.km || 0}
                label={t('pages.monthlySummary.children.km', {
                  replace: { CURRENCY: currencySymbol },
                })}
                onChangeNumber={(km) =>
                  setMonthlyOverviews(
                    monthlyReceipts.map((receipt) =>
                      receipt.id === monthlyReceipt.id
                        ? {
                            ...receipt,
                            travelCostTaxFree: {
                              ...receipt.travelCostTaxFree,
                              km: km,
                            },
                          }
                        : receipt,
                    ),
                  )
                }
              />
              <Input
                disabled={isLocked}
                labelBold={false}
                type="number"
                value={monthlyReceipt.travelCostTaxFree?.vma || 0}
                label={t('pages.monthlySummary.children.vma', {
                  replace: { CURRENCY: currencySymbol },
                })}
                onChangeNumber={(vma) =>
                  setMonthlyOverviews(
                    monthlyReceipts.map((receipt) =>
                      receipt.id === monthlyReceipt.id
                        ? {
                            ...receipt,
                            travelCostTaxFree: {
                              ...receipt.travelCostTaxFree,
                              vma,
                            },
                          }
                        : receipt,
                    ),
                  )
                }
              />
            </div>
          </div>
        </div>
        <div className="receipt-edit-form__right-section">
          <div className="receipt-detail-box__box__50">
            <p>{t('pages.monthlySummary.children.bonusPayment')}</p>
            <div className="receipt-detail-box__box__bonus-payment-inputs">
              {monthlyReceipt.bonusPayouts.map((payout) => {
                return (
                  <div className="receipt-detail-box__box__bonus-payment-inputs__entry">
                    <Input
                      disabled={isLocked}
                      type="text"
                      value={payout.description}
                      onChange={(description) => {
                        setMonthlyOverviews(
                          monthlyReceipts.map((receipt) => {
                            return receipt.id === monthlyReceipt.id
                              ? {
                                  ...receipt,
                                  bonusPayouts: receipt.bonusPayouts.map((bonusPayout) =>
                                    bonusPayout.id === payout.id
                                      ? { ...bonusPayout, description }
                                      : bonusPayout,
                                  ),
                                }
                              : receipt;
                          }),
                        );
                      }}
                    />
                    <Input
                      disabled={isLocked}
                      type="number"
                      value={payout.amount}
                      onChangeNumber={(amount) =>
                        setMonthlyOverviews(
                          monthlyReceipts.map((receipt) => {
                            return receipt.id === monthlyReceipt.id
                              ? {
                                  ...receipt,
                                  bonusPayouts: receipt.bonusPayouts.map((bonusPayout) =>
                                    bonusPayout.id === payout.id
                                      ? { ...bonusPayout, amount }
                                      : bonusPayout,
                                  ),
                                }
                              : receipt;
                          }),
                        )
                      }
                    />
                    <div className="receipt-detail-box__box__bonus-payment-delete">
                      <IconComponent
                        type={IconType.DELETE}
                        className={isLocked ? 'disabled' : 'clickable'}
                        width={35}
                        color={'white'}
                        onClick={() =>
                          !isLocked &&
                          setMonthlyOverviews(
                            monthlyReceipts.map((receipt) => {
                              return receipt.id === monthlyReceipt.id
                                ? {
                                    ...receipt,
                                    bonusPayouts: monthlyReceipt.bonusPayouts.filter(
                                      (oldPayout) => oldPayout.id !== payout.id,
                                    ),
                                  }
                                : receipt;
                            }),
                          )
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              maxWidth={'none'}
              disabled={isLocked}
              value={
                <div className="receipt-detail-box__button-value-wrapper">
                  <IconComponent type={IconType.ADD} width={25} color="white" />
                  <p>{t('general.buttons.add')}</p>
                </div>
              }
              onClick={() => {
                setMonthlyOverviews(
                  monthlyReceipts.map((receipt) =>
                    receipt.id === monthlyReceipt.id
                      ? {
                          ...receipt,
                          bonusPayouts: [...receipt.bonusPayouts, generateEmptyBonusPayout()],
                        }
                      : receipt,
                  ),
                );
              }}
            />
          </div>
          <div className="receipt-detail-box__box__50">
            <p>{t('pages.monthlySummary.children.addOns')}</p>
            <p>{t('pages.monthlySummary.children.description')}</p>
            <div className="receipt-detail-box__box__addOns">
              <p className="receipt-detail-box__box__addOns__label">
                {t('pages.monthlySummary.children.saturdayNight')}
              </p>
              <Input
                disabled={isLocked}
                value={monthlyReceipt.saturdayBonus + monthlyReceipt.nightBonus}
                type="number"
                label={t('pages.monthlySummary.children.hours')}
                onChangeNumber={(newBonus) =>
                  setMonthlyOverviews(
                    monthlyReceipts.map((receipt) =>
                      receipt.id === monthlyReceipt.id
                        ? {
                            ...receipt,
                            saturdayBonus: newBonus,
                            nightBonus: 0,
                          }
                        : receipt,
                    ),
                  )
                }
              />
            </div>
            <div className="receipt-detail-box__box__addOns">
              <p className="receipt-detail-box__box__addOns__label">
                {t('pages.monthlySummary.children.sunday')}
              </p>
              <Input
                disabled={isLocked}
                type="number"
                value={monthlyReceipt.sundayBonus}
                onChangeNumber={(newBonus) => {
                  if (newBonus)
                    setMonthlyOverviews(
                      monthlyReceipts.map((receipt) =>
                        receipt.id === monthlyReceipt.id
                          ? {
                              ...receipt,
                              sundayBonus: newBonus,
                            }
                          : receipt,
                      ),
                    );
                }}
              />
            </div>

            <div className="receipt-detail-box__box__addOns">
              <p className="receipt-detail-box__box__addOns__label">
                {t('pages.monthlySummary.children.holiday100')}
              </p>
              <Input
                disabled={isLocked}
                type="number"
                value={monthlyReceipt.holidayBonus100}
                onChangeNumber={(newBonus) => {
                  if (newBonus)
                    setMonthlyOverviews(
                      monthlyReceipts.map((receipt) =>
                        receipt.id === monthlyReceipt.id
                          ? {
                              ...receipt,
                              holidayBonus100: newBonus,
                            }
                          : receipt,
                      ),
                    );
                }}
              />
            </div>

            <div className="receipt-detail-box__box__addOns">
              <p className="receipt-detail-box__box__addOns__label">
                {t('pages.monthlySummary.children.holiday150')}
              </p>
              <Input
                disabled={isLocked}
                type="number"
                value={monthlyReceipt.holidayBonus150}
                onChangeNumber={(newBonus) => {
                  if (newBonus)
                    setMonthlyOverviews(
                      monthlyReceipts.map((receipt) =>
                        receipt.id === monthlyReceipt.id
                          ? {
                              ...receipt,
                              holidayBonus150: newBonus,
                            }
                          : receipt,
                      ),
                    );
                }}
              />
            </div>
            <div className="receipt-detail-box__box__addOns">
              <p className="receipt-detail-box__box__addOns__label">
                {t('pages.monthlySummary.children.overTime')}
              </p>
              <Input type="number" value={monthlyReceipt.payedOutSaldo / 60} disabled />
            </div>
            <Button
              maxWidth={'none'}
              disabled={isLocked}
              value={
                <div className="receipt-detail-box__button-value-wrapper">
                  <IconComponent
                    type={monthlyReceipt.payedOutSaldo !== 0 ? IconType.EDIT : IconType.ADD}
                    style={{ color: 'white' }}
                    width={25}
                  />
                  <p>
                    {monthlyReceipt.payedOutSaldo !== 0
                      ? t('pages.monthlySummary.children.overTimeEdit')
                      : t('pages.monthlySummary.children.overTimePayout')}
                  </p>
                </div>
              }
              onClick={() => toggleAzkReductionPopup(true)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default ReceiptDetailBox;
